import { Model, RepairService } from '../types/BookingTypes';

export interface SEOContent {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  structuredData: object;
}

export const generateSEOUrl = (model: Model): string => {
  const brandName = model.brand || '';
  const modelName = model.name || '';
  
  // Create SEO-friendly path with screen-replacement-price format
  const brandSlug = brandName.toLowerCase().replace(/\s+/g, '-');
  const modelSlug = modelName.toLowerCase().replace(/\s+/g, '-');
  const seoFriendlyPath = `${brandSlug}-${modelSlug}-screen-replacement-price`;
  
  return `/services/${model.id}/${seoFriendlyPath}`;
};

export const generateSEOContent = (model: Model, services: RepairService[], modelId: string): SEOContent => {
  const brandName = model.brand || '';
  const modelName = model.name || '';
  
  // Check if screen/display services are available
  const hasScreenService = services.some(service => 
    service.serviceName.toLowerCase().includes('screen') || 
    service.serviceName.toLowerCase().includes('display')
  );
  
  // Check for other common services
  const hasBatteryService = services.some(service => 
    service.serviceName.toLowerCase().includes('battery')
  );
  
  const hasChargingService = services.some(service => 
    service.serviceName.toLowerCase().includes('charging') ||
    service.serviceName.toLowerCase().includes('port')
  );
  
  let title = '';
  let description = '';
  let keywords = '';
  
  if (hasScreenService) {
    title = `${brandName} ${modelName} Screen Replacement Price in Mumbai | Doorstep Repair | Minto`;
    description = `Get ${brandName} ${modelName} screen replacement at your doorstep in Mumbai — Andheri, Bandra, Dadar, Borivali & more. Genuine parts, 90-day warranty. Check price & book now!`;
    keywords = `${brandName} ${modelName} screen replacement, ${brandName} ${modelName} screen replacement price Mumbai, ${brandName} ${modelName} display repair Mumbai, ${brandName} ${modelName} screen repair Andheri, ${brandName} ${modelName} screen repair Bandra, ${brandName} ${modelName} screen repair Dadar, ${modelName} screen replacement price, doorstep screen repair Mumbai, mobile screen repair Mumbai`;
  } else if (hasBatteryService) {
    title = `${brandName} ${modelName} Battery Replacement Price in Mumbai | Doorstep Service | Minto`;
    description = `${brandName} ${modelName} battery replacement at your doorstep in Mumbai — Andheri, Bandra, Dadar, Borivali & all areas. Genuine battery, 90-day warranty. Check price & book now!`;
    keywords = `${brandName} ${modelName} battery replacement, ${brandName} ${modelName} battery replacement price Mumbai, ${brandName} ${modelName} battery repair Mumbai, ${brandName} ${modelName} battery replacement Andheri, ${brandName} ${modelName} battery replacement Dadar, ${brandName} ${modelName} battery replacement Bandra, ${modelName} battery replacement price, doorstep battery replacement Mumbai, mobile battery repair Mumbai`;
  } else if (hasChargingService) {
    title = `${brandName} ${modelName} Charging Port Repair in Mumbai | Doorstep Fix | Minto`;
    description = `Fix ${brandName} ${modelName} charging port issues at your doorstep in Mumbai. Expert repair across Andheri, Bandra, Dadar, Borivali & more. Warranty included. Book now!`;
    keywords = `${brandName} ${modelName} charging port repair, ${brandName} ${modelName} charging port repair Mumbai, ${brandName} ${modelName} charging issue Mumbai, ${modelName} charging port repair Mumbai, doorstep charging port fix Mumbai, mobile charging repair Mumbai`;
  } else {
    title = `${brandName} ${modelName} Repair in Mumbai | Doorstep Mobile Repair | Minto`;
    description = `Professional ${brandName} ${modelName} repair at your doorstep in Mumbai — screen, battery, charging port & more. Serving Andheri, Bandra, Dadar, Borivali. 90-day warranty. Book now!`;
    keywords = `${brandName} ${modelName} repair Mumbai, ${brandName} ${modelName} service Mumbai, ${modelName} repair Mumbai, doorstep mobile repair Mumbai, phone repair Mumbai, ${brandName} repair Andheri, ${brandName} repair Bandra, ${brandName} repair Dadar`;
  }
  
  // Generate SEO-friendly URL path
  const brandSlug = brandName.toLowerCase().replace(/\s+/g, '-');
  const modelSlug = modelName.toLowerCase().replace(/\s+/g, '-');
  const seoFriendlyPath = `${brandSlug}-${modelSlug}-screen-replacement-price`;
  const canonicalUrl = `https://www.mintorepairs.com/services/${modelId}/${seoFriendlyPath}`;
  
  // Structured data for local business and service
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": canonicalUrl,
        "name": `${brandName} ${modelName} Repair`,
        "description": description,
        "provider": {
          "@type": "LocalBusiness",
          "name": "Minto Mobile Repair",
          "url": "https://www.mintorepairs.com",
          "telephone": "+919324299258",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Mumbai",
            "addressRegion": "Maharashtra",
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "19.0760",
            "longitude": "72.8777"
          },
          "openingHours": "Mo-Su 08:00-20:00",
          "priceRange": "₹₹"
        },
        "areaServed": [
          { "@type": "Place", "name": "Mumbai" },
          { "@type": "Place", "name": "Andheri" },
          { "@type": "Place", "name": "Bandra" },
          { "@type": "Place", "name": "Dadar" },
          { "@type": "Place", "name": "Borivali" },
          { "@type": "Place", "name": "Thane" },
          { "@type": "Place", "name": "Navi Mumbai" },
          { "@type": "Place", "name": "Kalyan" },
          { "@type": "Place", "name": "Dombivli" }
        ],
        "serviceType": `${brandName} ${modelName} Mobile Repair`,
        "category": "Mobile Phone Repair",
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceRange": "₹299-₹4999",
          "priceCurrency": "INR",
          "validFrom": new Date().toISOString(),
          "areaServed": {
            "@type": "Place",
            "name": "Mumbai — Andheri, Bandra, Dadar, Borivali, Thane, Navi Mumbai"
          }
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://www.mintorepairs.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": `${brandName} Repair`,
            "item": `https://www.mintorepairs.com/brand/${model.brandId}`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": `${brandName} ${modelName} Repair`,
            "item": canonicalUrl
          }
        ]
      }
    ]
  };
  
  return {
    title,
    description,
    keywords,
    canonicalUrl,
    structuredData
  };
};

export const updatePageSEO = (seoContent: SEOContent) => {
  // Update page title
  document.title = seoContent.title;
  
  // Update meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', seoContent.description);
  }
  
  // Update meta keywords
  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }
  metaKeywords.setAttribute('content', seoContent.keywords);
  
  // Update Open Graph meta tags
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) {
    ogTitle.setAttribute('content', seoContent.title);
  }
  
  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription) {
    ogDescription.setAttribute('content', seoContent.description);
  }
  
  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) {
    ogUrl.setAttribute('content', seoContent.canonicalUrl);
  }
  
  // Update Twitter meta tags
  const twitterTitle = document.querySelector('meta[property="twitter:title"]');
  if (twitterTitle) {
    twitterTitle.setAttribute('content', seoContent.title);
  }
  
  const twitterDescription = document.querySelector('meta[property="twitter:description"]');
  if (twitterDescription) {
    twitterDescription.setAttribute('content', seoContent.description);
  }
  
  const twitterUrl = document.querySelector('meta[property="twitter:url"]');
  if (twitterUrl) {
    twitterUrl.setAttribute('content', seoContent.canonicalUrl);
  }
  
  // Update canonical URL
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', seoContent.canonicalUrl);
  }
  
  // Add structured data
  const existingScript = document.querySelector('#seo-structured-data');
  if (existingScript) {
    existingScript.remove();
  }
  
  const script = document.createElement('script');
  script.id = 'seo-structured-data';
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(seoContent.structuredData);
  document.head.appendChild(script);
};

export const resetPageSEO = () => {
  // Reset to default title
  document.title = 'Doorstep Mobile Repair in Mumbai | Screen, Battery & More | Minto';

  // Reset meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', 'Top-rated doorstep mobile repair in Mumbai — Andheri, Bandra, Dadar, Borivali & more. Screen replacement, battery repair, charging port fix. Certified technicians, 90-day warranty. Book now!');
  }

  // Reset canonical URL
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', 'https://www.mintorepairs.com');
  }
  
  // Remove structured data
  const existingScript = document.querySelector('#seo-structured-data');
  if (existingScript) {
    existingScript.remove();
  }
}; 